/**
 * API-level Zod schemas for CourseMaterial resource.
 * Mirrors submission-file-schemas.ts pattern for R2-backed uploads.
 * Bucket: lms-materials (R2_MATERIALS binding).
 */

import { z } from 'zod';
import { optionalString } from './zod-helpers';

export const courseMaterialType = z.enum(['file', 'link', 'video', 'document', 'audio', 'slide']);

// ---------------------------------------------------------------------------
// Allowed MIME types for R2-backed uploads (enforced at service layer)
// ---------------------------------------------------------------------------
export const ALLOWED_CONTENT_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  // Images
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  // Video
  'video/mp4',
  'video/webm',
  'video/ogg',
  // Audio
  'audio/mpeg',
  'audio/ogg',
  'audio/wav',
  'audio/webm',
] as const;

/** 200 MB max per file */
export const MAX_FILE_BYTES = 200 * 1024 * 1024;

// ---------------------------------------------------------------------------
// CRUD schemas
// ---------------------------------------------------------------------------
export const createCourseMaterialSchema = z.object({
  courseLessonId: z.string().min(1),
  type: courseMaterialType,
  title: z.string().min(1).max(300),
  // Required for link/video types; set by service after R2 upload confirm for file types
  url: z.string().url(),
  description: optionalString(),
  sortOrder: z.number().int().nonnegative().optional(),
});

export const updateCourseMaterialSchema = z.object({
  type: courseMaterialType.optional(),
  title: z.string().min(1).max(300).optional(),
  url: z.string().url().optional(),
  description: optionalString(),
  sortOrder: z.number().int().nonnegative().optional(),
});

export const listCourseMaterialsSchema = z.object({
  page: z.coerce.number().int().positive().optional(),
  pageSize: z.coerce.number().int().positive().max(100).optional(),
  courseLessonId: z.string().optional(),
  type: courseMaterialType.optional(),
});

// ---------------------------------------------------------------------------
// R2 upload flow schemas
// ---------------------------------------------------------------------------

/** Step 1 — client requests a pre-signed upload URL */
export const uploadUrlRequestSchema = z.object({
  courseLessonId: z.string().min(1),
  type: courseMaterialType,
  title: z.string().min(1).max(300),
  fileName: z.string().min(1).max(500),
  contentType: z.string().refine(
    (ct) => ALLOWED_CONTENT_TYPES.includes(ct as (typeof ALLOWED_CONTENT_TYPES)[number]),
    { message: 'Content type not allowed' }
  ),
  fileSize: z.number().int().positive().max(MAX_FILE_BYTES, {
    message: `File must be ≤ ${MAX_FILE_BYTES / 1024 / 1024} MB`,
  }),
  description: optionalString(),
});

/** Step 2 — client confirms upload completed; service sets fileKey + fileSize on DB row */
export const confirmUploadSchema = z.object({
  materialId: z.string().min(1),
  fileKey: z.string().min(1),
});

export type CreateCourseMaterialDto = z.infer<typeof createCourseMaterialSchema>;
export type UpdateCourseMaterialDto = z.infer<typeof updateCourseMaterialSchema>;
export type ListCourseMaterialsDto = z.infer<typeof listCourseMaterialsSchema>;
export type UploadUrlRequestDto = z.infer<typeof uploadUrlRequestSchema>;
export type ConfirmUploadDto = z.infer<typeof confirmUploadSchema>;
